import type { DealOrderType, ProcessingStep, SearchBargain } from '@/types/deals';
import type { NotificationItem } from '@/types/notifications';

// Чистая доменная логика дашборда (главной кабинета). Без React/JSX — покрывается юнит-тестами.
// Источник истины «чей ход» — processingStep сделки + моя сторона (вкладка списка):
// SALE → я продавец, PURCHASE → я покупатель. Шаги, не требующие моего действия
// (*_PROCESSING / *_GENERATION / экспедитор / завершённые), в ленту не попадают.

type Actor = 'SELLER' | 'BUYER' | 'AGENT';

interface StepAction {
  readonly actor: Actor;
  readonly labelKey: string;
  /** Финансовое действие (доплата/возврат) — для отдельной иконки/приоритета. */
  readonly settlement?: boolean;
}

// Только actionable-шаги: кто действует и i18n-ключ действия. Остальные processingStep → ожидание.
const ACTION_MAP: Partial<Record<ProcessingStep, StepAction>> = {
  WAIT_SELLER_SPEC_SIGNED: { actor: 'SELLER', labelKey: 'app.home.action.signSpec' },
  WAIT_BUYER_SPEC_SIGNED: { actor: 'BUYER', labelKey: 'app.home.action.signSpec' },
  WAIT_AGENT_SPEC_SIGNED: { actor: 'AGENT', labelKey: 'app.home.action.signSpec' },
  WAIT_SELLER_SEND_SCHEDULE: { actor: 'SELLER', labelKey: 'app.home.action.sendSchedule' },
  WAIT_SELLER_CONFIRM_SCHEDULE: { actor: 'SELLER', labelKey: 'app.home.action.confirmSchedule' },
  WAIT_BUYER_CONFIRM_SCHEDULE: { actor: 'BUYER', labelKey: 'app.home.action.confirmSchedule' },
  WAIT_SHIPMENT_IS_READY_CONFIRMATION: { actor: 'SELLER', labelKey: 'app.home.action.confirmShipmentReady' },
  WAIT_CONSIGNEE_ASSIGNED: { actor: 'BUYER', labelKey: 'app.home.action.assignConsignee' },
  WAIT_AGENT_FORWARDER_ASSIGNMENT_SIGNED: { actor: 'AGENT', labelKey: 'app.home.action.signForwarderInstruction' },
  WAIT_DELIVERY: { actor: 'BUYER', labelKey: 'app.home.action.confirmDelivery' },
  WAIT_ATTESTATION: { actor: 'BUYER', labelKey: 'app.home.action.attest' },
  WAIT_DECLARATION_SENDING: { actor: 'SELLER', labelKey: 'app.home.action.sendDeclaration' },
  WAIT_UPLOAD_SIGNED_UPD_TORG12: { actor: 'SELLER', labelKey: 'app.home.action.signUpd' },
  WAIT_UPLOAD_UNDERPAYMENT: { actor: 'BUYER', labelKey: 'app.home.action.payUnderpayment', settlement: true },
  WAIT_WIRE_UNDERPAYMENT_CONFIRMATION: { actor: 'SELLER', labelKey: 'app.home.action.confirmPaymentReceived', settlement: true },
  WAIT_UPLOAD_OVERPAYMENT: { actor: 'SELLER', labelKey: 'app.home.action.returnOverpayment', settlement: true },
  WAIT_RETURN_OVERPAYMENT_CONFIRMATION: { actor: 'BUYER', labelKey: 'app.home.action.confirmReturnReceived', settlement: true },
};

/** Что должен сделать Я по сделке сейчас (ключ действия + settlement), либо null — не мой ход.
 *  `actor` — моя роль в сделке: продавец/покупатель (по стороне вкладки) либо агент (посредник). */
export function myActionFor(step: ProcessingStep | undefined, actor: Actor): StepAction | null {
  if (!step) {
    return null;
  }
  const action = ACTION_MAP[step];
  return action && action.actor === actor ? action : null;
}

// «Новая» = свежесозданная сделка на раннем шаге и ещё в работе. Включает первый шаг WAIT_ATTESTATION:
// с него начинается сделка, и на нём одна из сторон/агент ещё не действует — но должна видеть, что
// сделка появилась (в ленте «Требует внимания» как информационный элемент, см. buildAttention).
const EARLY_STEPS = new Set<ProcessingStep>([
  'BARGAIN_CREATED',
  'WAIT_ATTESTATION',
  'WAIT_BUYER_SPEC_SIGNED',
  'WAIT_SELLER_SPEC_SIGNED',
  'WAIT_AGENT_SPEC_SIGNED',
  'SPECIFICATION_COMPLETED',
]);
const NEW_DEAL_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

export function isNewDeal(item: SearchBargain, nowMs: number): boolean {
  if (item.status !== 'IN_PROGRESS' || !item.processingStep || !EARLY_STEPS.has(item.processingStep)) {
    return false;
  }
  // createdDate приходит от бэкенда в формате «dd.MM.yyyy» (order-service DateUtils.toString). Сырой
  // new Date("04.08.2026") читает её как MM.DD → 8 апреля вместо 4 августа → сделка ложно «стара» и
  // выпадает из окна новизны. Парсим через parseDeadline (понимает и dd.MM.yyyy, и ISO).
  const created = parseDeadline(item.createdDate);
  return created !== null && nowMs - created <= NEW_DEAL_WINDOW_MS;
}

/**
 * Просрочка сделки, ещё находящейся в работе. Сигнал — `schedule.finishDelays`: авторитетная задержка
 * в днях, которую считает бэкенд (order-service `ShipmentScheduleUtils.calculateBargainFinishDelay`):
 * >0 только когда сделка реально опаздывает по отгрузке/экспедированию, 0 — когда идёт в срок или раньше.
 *
 * Важно: НЕ сравниваем `plannedFinishDate < today` вручную. `plannedFinishDate` — плановый финиш ОТГРУЗКИ
 * (середина сделки), поэтому любая нормально идущая сделка в пост-отгрузочной стадии (декларация, оплата,
 * взаиморасчёты) оказалась бы «за датой отгрузки» и ложно помечалась просроченной. `finishDelays` этой
 * ловушки лишён — он равен 0, пока нет фактического опоздания.
 */
export function isOverdue(item: SearchBargain): boolean {
  return item.status === 'IN_PROGRESS' && (item.schedule?.finishDelays ?? 0) > 0;
}

export interface DealWithSide {
  readonly side: DealOrderType;
  readonly item: SearchBargain;
}

export type AttentionKind = 'blocked' | 'overdue' | 'settlement' | 'action' | 'info';

export interface AttentionItem {
  readonly key: string;
  readonly dealId: number;
  /** Отображаемый № сделки (контракт или «№id»). */
  readonly dealNo: string;
  readonly kind: AttentionKind;
  /** Меньше — выше в списке. */
  readonly priority: number;
  /** Готовый текст (для ошибок-приостановок из уведомлений). Взаимоисключим с actionLabelKey. */
  readonly title?: string;
  /** i18n-ключ действия (для «мой ход»). */
  readonly actionLabelKey?: string;
  readonly goods?: string;
  readonly date?: string;
}

const MAX_ATTENTION = 8;

function dealNoOf(item: SearchBargain): string {
  const contract = item.contractNum?.trim();
  return contract && contract.length > 0 ? contract : `№${item.id}`;
}

/** Приостановки/ошибки из ленты уведомлений (messageStatus=ERROR + bargainId) → приоритет 1. */
export function errorAttention(notifications: readonly NotificationItem[] | undefined): AttentionItem[] {
  const out: AttentionItem[] = [];
  for (const notification of notifications ?? []) {
    const bargainId = notification.payload?.bargainId;
    if (notification.payload?.messageStatus === 'ERROR' && typeof bargainId === 'number') {
      out.push({
        key: `err-${bargainId}`,
        dealId: bargainId,
        dealNo: `№${bargainId}`,
        kind: 'blocked',
        priority: 1,
        title: notification.payload?.messageHeader,
        date: notification.createdDate,
      });
    }
  }
  return out;
}

/**
 * Лента «требует внимания»: ошибки-приостановки (prio 1) + просрочки (overdue, prio 1, всем ролям) +
 * сделки, где мой ход (settlement prio 2, прочие действия prio 3) + новые сделки (info, prio 4). Дедуп по
 * сделке (одна сделка — один элемент; ошибка/просрочка важнее обычного действия). Сортировка — по СВЕЖЕСТИ
 * (новые сверху): дата события/сделки ЧИСЛЕННО по убыванию, приоритет лишь тай-брейк при равной дате.
 * Даты парсим через parseDeadline: у сделок формат «dd.MM.yyyy», строковое сравнение упорядочило бы по дню
 * месяца, а не по реальной дате. Ограничение MAX_ATTENTION. Дата: у ошибок — время уведомления, у просрочек —
 * плановый срок, у действий/новых — {@code createdDate} сделки (иного признака свежести у сделки нет).
 */
export function buildAttention(
  deals: readonly DealWithSide[],
  notifications: readonly NotificationItem[] | undefined,
  isAgent = false,
  nowMs = Date.now(),
): AttentionItem[] {
  const items = errorAttention(notifications);
  const seen = new Set<number>(items.map((item) => item.dealId));

  for (const { side, item } of deals) {
    if (item.id == null || seen.has(item.id)) {
      continue;
    }
    // Роль в сделке: агент — посредник (actor AGENT); иначе продавец/покупатель по стороне вкладки.
    const actor: Actor = isAgent ? 'AGENT' : side === 'SALE' ? 'SELLER' : 'BUYER';
    // Просрочка показывается ЛЮБОЙ роли, включая агента: хотя агент пассивен, опоздание по сопровождаемой
    // сделке ему важно (координирует стороны). Просрочка критична и перекрывает обычный «мой ход» по этой
    // сделке — важнее показать, что срок истёк, чем конкретное действие (оно видно на карточке сделки).
    if (isOverdue(item)) {
      seen.add(item.id);
      // Если по сделке есть мой ход — кладём его ключ, чтобы показать совмещённо «<действие> · просрочено»;
      // если хода нет (жду контрагента/этап автоматический) — actionLabelKey пуст, покажем «срок истёк».
      const overdueAction = myActionFor(item.processingStep, actor);
      items.push({
        key: `overdue-${item.id}`,
        dealId: item.id,
        dealNo: dealNoOf(item),
        kind: 'overdue',
        priority: 1,
        actionLabelKey: overdueAction?.labelKey,
        goods: item.goodsType,
        date: item.schedule?.plannedFinishDate,
      });
      continue;
    }
    const action = myActionFor(item.processingStep, actor);
    if (!action) {
      // Ход сейчас не мой, но если сделка НОВАЯ (свежая, на раннем шаге) — показываем её информационным
      // элементом «новая сделка» ЛЮБОЙ роли (продавец/покупатель/агент): каждый участник должен сразу
      // видеть, что сделка появилась. Без призыва к действию и ниже приоритетом любых «моих ходов»
      // (priority 4). Дедуп с секцией «Новые сделки» — через newDealsForList (исключает попавшие в ленту).
      if (isNewDeal(item, nowMs)) {
        seen.add(item.id);
        items.push({
          key: `new-${item.id}`,
          dealId: item.id,
          dealNo: dealNoOf(item),
          kind: 'info',
          priority: 4,
          actionLabelKey: 'app.home.action.newDeal',
          goods: item.goodsType,
          date: item.createdDate,
        });
      }
      continue;
    }
    seen.add(item.id);
    items.push({
      key: `act-${item.id}`,
      dealId: item.id,
      dealNo: dealNoOf(item),
      kind: action.settlement ? 'settlement' : 'action',
      priority: action.settlement ? 2 : 3,
      actionLabelKey: action.labelKey,
      goods: item.goodsType,
      date: item.createdDate,
    });
  }

  return items
    .sort((a, b) => (parseDeadline(b.date) ?? 0) - (parseDeadline(a.date) ?? 0) || a.priority - b.priority)
    .slice(0, MAX_ATTENTION);
}

/** Число новых сделок (для бейджа плитки «Сделки»). */
export function countNewDeals(deals: readonly DealWithSide[], nowMs: number): number {
  return deals.reduce((sum, { item }) => sum + (isNewDeal(item, nowMs) ? 1 : 0), 0);
}

/** Новые сделки, которых нет в ленте «внимание» (там они показаны как «мой ход»). */
export function newDealsForList(
  deals: readonly DealWithSide[],
  attention: readonly AttentionItem[],
  nowMs: number,
): DealWithSide[] {
  const inAttention = new Set(attention.map((a) => a.dealId));
  return deals.filter(({ item }) => item.id != null && !inAttention.has(item.id) && isNewDeal(item, nowMs));
}

// ── Ближайшие сроки по сделкам (плановое завершение отгрузки) ────────────────────
// Источник — schedule.plannedFinishDate из уже загруженного списка (без доп. запросов).

const RU_DATE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

/** Дата-строка (ISO или dd.MM.yyyy) → ms, либо null. */
export function parseDeadline(value: string | undefined): number | null {
  if (!value) {
    return null;
  }
  const ru = RU_DATE.exec(value.trim());
  const ms = Date.parse(ru ? `${ru[3]}-${ru[2]}-${ru[1]}` : value);
  return Number.isNaN(ms) ? null : ms;
}

function startOfDay(ms: number): number {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

const DAY_MS = 24 * 60 * 60 * 1000;

export type DeadlineUrgency = 'today' | 'tomorrow' | 'date';

function urgencyOf(dateMs: number, nowMs: number): DeadlineUrgency {
  const days = Math.round((startOfDay(dateMs) - startOfDay(nowMs)) / DAY_MS);
  if (days <= 0) {
    return 'today';
  }
  return days === 1 ? 'tomorrow' : 'date';
}

export interface UpcomingDeadline {
  readonly dealId: number;
  readonly dealNo: string;
  readonly goods?: string;
  readonly dateMs: number;
  readonly urgency: DeadlineUrgency;
}

const DEADLINE_WINDOW_DAYS = 14;
const MAX_DEADLINES = 5;

/**
 * Предстоящие сроки (плановое завершение отгрузки) в окне DEADLINE_WINDOW_DAYS, по возрастанию.
 * Только активные сделки; прошедшие/далёкие/без даты — отбрасываем; дедуп по сделке; cap MAX_DEADLINES.
 */
export function upcomingDeadlines(
  deals: readonly DealWithSide[],
  nowMs: number,
  withinDays: number = DEADLINE_WINDOW_DAYS,
  max: number = MAX_DEADLINES,
): UpcomingDeadline[] {
  const today = startOfDay(nowMs);
  const horizon = today + withinDays * DAY_MS;
  const seen = new Set<number>();
  const out: UpcomingDeadline[] = [];

  for (const { item } of deals) {
    if (item.id == null || seen.has(item.id) || item.status !== 'IN_PROGRESS') {
      continue;
    }
    const ms = parseDeadline(item.schedule?.plannedFinishDate);
    if (ms == null) {
      continue;
    }
    const day = startOfDay(ms);
    if (day < today || day > horizon) {
      continue;
    }
    seen.add(item.id);
    out.push({
      dealId: item.id,
      dealNo: dealNoOf(item),
      goods: item.goodsType,
      dateMs: ms,
      urgency: urgencyOf(ms, nowMs),
    });
  }

  return out.sort((a, b) => a.dateMs - b.dateMs).slice(0, max);
}
